import React, { useContext, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { store } from "../../config/store/store";
import AuthContext from "../../context/AuthContext";
import SearchableSelectField from "./SearchableSelectField";
import { getCountries } from "./CountrySelectField.thunk";
import { useEffectOnce } from "../../utils/effects";
import { CountrySelectFieldContainer } from "./CountrySelectField.styles";

/**
 * Country autocomplete backed by GET /countries (full DB list).
 * Formik value remains ISO-3166-1 alpha-2 (e.g. "US").
 *
 * @param {string[]} [includeCountryCodes] - When set, only these ISO-2 codes are shown.
 * @param {string[]} [excludeCountryCodes] - ISO-2 codes to hide (ignored when includeCountryCodes is set).
 */
const CountrySelectField = ({
  name,
  value,
  onChange,
  onBlur,
  label = "Country",
  required = false,
  error = "",
  touched = false,
  disabled = false,
  includeCountryCodes = null,
  excludeCountryCodes = [],
  sx = {},
  // Kept for API compatibility with callers that passed Select props (unused).
  selectProps: _selectProps,
  menuItemProps: _menuItemProps,
  ..._otherProps
}) => {
  const dispatch = useDispatch();
  const { token } = useContext(AuthContext);
  const { countries, status } = useSelector((state) => state.countrySelectField);

  const isLoading = status === "loading" && countries.length === 0;

  useEffectOnce(() => {
    if (!token) return;

    const currentState = store.getState();
    const { countries: storeCountries, status: storeStatus } =
      currentState.countrySelectField;

    if (
      storeCountries.length === 0 &&
      storeStatus !== "loading" &&
      storeStatus !== "succeeded"
    ) {
      dispatch(getCountries({ token }));
    }
  });

  const options = useMemo(() => {
    if (!countries?.length) return [];
    let list = countries;
    if (Array.isArray(includeCountryCodes) && includeCountryCodes.length > 0) {
      const allowed = new Set(
        includeCountryCodes.map((c) => String(c).toUpperCase()),
      );
      list = countries.filter((c) =>
        allowed.has(String(c.value).toUpperCase()),
      );
    } else if (excludeCountryCodes?.length) {
      const blocked = new Set(
        excludeCountryCodes.map((c) => String(c).toUpperCase()),
      );
      list = countries.filter(
        (c) => !blocked.has(String(c.value).toUpperCase()),
      );
    }
    return list.map((c) => {
      const code = String(c.value || "").toUpperCase();
      const nameLabel = c.label || code;
      return {
        id: code,
        label: nameLabel,
        description: nameLabel,
      };
    });
  }, [countries, includeCountryCodes, excludeCountryCodes]);

  const normalizedValue = value ? String(value).toUpperCase() : "";

  const resolvedOption = useMemo(() => {
    if (!normalizedValue) return null;
    const fromList = options.find((opt) => opt.id === normalizedValue);
    if (fromList) return fromList;
    return { id: normalizedValue, label: normalizedValue, description: normalizedValue };
  }, [normalizedValue, options]);

  return (
    <CountrySelectFieldContainer sx={sx}>
      <SearchableSelectField
        name={name}
        label={label}
        value={normalizedValue}
        onChange={onChange}
        onBlur={onBlur}
        options={options}
        resolvedOption={resolvedOption}
        loading={isLoading}
        clientSideFilter
        required={required}
        error={error}
        touched={touched}
        disabled={disabled}
        placeholder="Search country…"
        noOptionsText="No countries match. Try another search."
        sx={{}}
      />
    </CountrySelectFieldContainer>
  );
};

export default CountrySelectField;
